import { Badge, Button, Offcanvas } from 'react-bootstrap';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MouseEvent } from 'react';
import type { DirLeadStatusType } from '@econtractor/types';
import { defLeadsStatus, useLead } from '../context/LeadProvider';
import { useSettings } from 'context/SettingsContext';
import { isSameIdSet } from 'utils/helpers/list/isSameIdSet';
import vconst from 'utils/helpers/structs/vconst';
import { tDirName } from 'utils/helpers/i18n/tDirName';
import SolarIcon from 'components/icons/SolarIcon';
import EToolTip from 'components/others/EToolTip';

const LeadFilterStatus = () => {
    const { t } = useTranslation(['Leads', 'common']);
    const { leadsSatus, setLeadStatus } = useLead();
    const { localSettings } = useSettings();
    const [show, setShow] = useState(false);
    const [draftStatus, setDraftStatus] = useState<number[]>([...leadsSatus]);
    const filterActive = !isSameIdSet(leadsSatus, defLeadsStatus);

    const handleHide = () => setShow(false);

    const handleOpen = () => {
        setDraftStatus([...leadsSatus]);
        setShow(true);
    };

    const handleClickStatus = (e: MouseEvent<HTMLElement>, statusID: number) => {
        e.currentTarget.blur();
        setDraftStatus((prev) =>
            prev.includes(statusID) ? prev.filter((ID) => ID !== statusID) : [...prev, statusID],
        );
    };

    const handleApply = () => {
        setLeadStatus([...draftStatus]);
        handleHide();
    };

    const handleReset = () => {
        setLeadStatus([...defLeadsStatus]);
        handleHide();
    };

    return (
        <>
            <EToolTip title={t('toolLeadFilter')}>
                <span className="d-inline-block">
                    <Button
                        className="fs-14 px-2"
                        size="sm"
                        variant={filterActive ? 'danger' : 'primary'}
                        onClick={(e) => {
                            e.currentTarget.blur();
                            handleOpen();
                        }}
                    >
                        <SolarIcon name="funnel" className="fs-14 p-0" />
                    </Button>
                </span>
            </EToolTip>

            <Offcanvas show={show} onHide={handleHide} placement="end">
                <Offcanvas.Header closeButton className="border-bottom">
                    <Offcanvas.Title>{t('toolLeadFilter')}</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                    <div className="off-filter-form d-flex flex-column h-100">
                        <div className="off-filter-content px-3">
                            {localSettings?.DIRS?.LeadStatus &&
                                localSettings.DIRS.LeadStatus.map((lStatus: DirLeadStatusType) => {
                                    const statusStyle = lStatus.statusJson?.statusStyle || 'danger';
                                    const isPostponed = Number(lStatus.ID) === vconst.leadPostponed;
                                    return (
                                        <button
                                            type="button"
                                            key={lStatus.ID}
                                            className={`off-filter-item${isPostponed ? ' lead-filter-item--postponed' : ''}`}
                                            onClick={(e) => handleClickStatus(e, lStatus.ID)}
                                        >
                                            <div className="me-2">
                                                <input
                                                    type="checkbox"
                                                    checked={draftStatus.includes(lStatus.ID)}
                                                    onChange={() => null}
                                                    className="form-check-input"
                                                />
                                            </div>

                                            <div className="lead-filter-item__content">
                                                <Badge className="fs-12" bg={statusStyle}>
                                                    {tDirName(t, lStatus.i18nKey, lStatus.statusName) ||
                                                        t(`status.${lStatus.ID}`)}
                                                </Badge>
                                                {isPostponed ? (
                                                    <span className="lead-filter-item__hint">
                                                        {t('filterPostponedHint')}
                                                    </span>
                                                ) : null}
                                            </div>
                                        </button>
                                    );
                                })}
                        </div>
                        <div className="p-3 offcanvas-footer off-filter-footer">
                            <div className="d-flex justify-content-end gap-2">
                                <Button variant="secondary" onClick={handleReset}>
                                    {t('common:buttons.reset')}
                                </Button>
                                <Button variant="info" className="min100px" onClick={handleApply}>
                                    {t('common:buttons.apply')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
};

export default LeadFilterStatus;
